import * as React from 'react';
import {
  ColumnFiltersState,
  FilterFn,
  Row,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Box, useBoxes } from '@/lib/api/boxes';
import { RefreshCcw } from 'lucide-react';
import { Action, ActionButton } from './action-button';
import { boxActions } from './box-actions';
import { columns } from './columns';
import { Separator } from '@/components/ui/separator';
import { TableToolbar } from './table-toolbar';
import { createTask, TaskCommand } from '@/lib/api/task';
import { addAlertBox } from '@/lib/api/monitoring';
import { useLiveIps } from '@/lib/api/liveBoxes';
import { useIsAdmin } from '@/lib/hooks/useIsAdmin';
import { useNavigate } from 'react-router-dom';
import { useSessionState } from '@/lib/hooks/useSessionState';
import { toast } from 'sonner';
import { InstallApkDialog } from './install-apk-dialog';
import { AddBoxDialog } from './add-box-dialog';
import { apkMatchesVersion, useApks } from '@/lib/api/apks';
import { sendCcCommand } from '@/lib/api/remoteCommands';

// Not TaskCommands: CC toggles are direct ProductionCommands writes.
const CC_ON = 'CC_ON';
const CC_OFF = 'CC_OFF';
import { Switch } from '@/components/ui/switch';
import {
  setEngineeringMode,
  useEngineeringConfig,
} from '@/lib/api/engineering';

const myCustomGlobalFilterFn: FilterFn<Box> = (
  row: Row<Box>,
  _columnId: string,
  filterValue: string
) => {
  const searchableColumnIds = ['name', 'ip']; // Define the columns to search

  if (!filterValue) {
    return true; // No filter value, show all rows
  }

  const normalizedFilterValue = String(filterValue).toLowerCase();

  // Check if any of the specified columns contain the filter value
  return searchableColumnIds.some((id) => {
    const cellValue = row.getValue(id);
    return String(cellValue).toLowerCase().includes(normalizedFilterValue);
  });
};

// Stable identity while boxes load — a fresh [] each render would force
// TanStack Table to recompute all row models every render.
const NO_BOXES: Box[] = [];

export function BoxList() {
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const liveIps = useLiveIps();
  const [apks] = useApks();

  // version string -> labels of the labeled APKs embedding it, so the App
  // column can show the label bubble (e.g. NV-LATEST) instead of the raw
  // version when a box runs a labeled build.
  const labelsForVersion = React.useCallback(
    (version?: string) => {
      if (!version) return [];
      return (apks ?? [])
        .filter((a) => a.label && apkMatchesVersion(a, version))
        .map((a) => a.label as string);
    },
    [apks]
  );

  // Persisted in sessionStorage so search/sort/filters survive jumping to a
  // task page and coming back.
  const [sorting, setSorting] = useSessionState<SortingState>('boxes.sorting', [
    { id: 'name', desc: false },
  ]);
  const [globalFilter, setGlobalFilter] = useSessionState(
    'boxes.globalFilter',
    ''
  );
  const [columnFilters, setColumnFilters] = useSessionState<ColumnFiltersState>(
    'boxes.columnFilters',
    []
  );

  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [installApkOpen, setInstallApkOpen] = React.useState(false);
  const [values] = useBoxes();

  // Actions that aren't plain agent tasks live here rather than in the shared
  // boxActions list, which the task page reuses for payload-less re-runs.
  const actions = React.useMemo<Action[]>(() => {
    const list: Action[] = [...boxActions];
    // CC on/off are ProductionCommands FIELD_ROW writes ("|||cc_on"), acked
    // by the box app itself — no agent task involved.
    list.push(
      { label: 'CC On', value: CC_ON },
      { label: 'CC Off', value: CC_OFF }
    );
    // Install APK opens a dialog (which APK). Admin only.
    if (isAdmin) {
      list.push({ label: 'Install APK', value: TaskCommand.InstallApk });
    }
    return list;
  }, [isAdmin]);

  const table = useReactTable({
    data: values ?? NO_BOXES,
    columns,
    getRowId: (row) => row.ip,
    onSortingChange: setSorting,
    globalFilterFn: myCustomGlobalFilterFn,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    meta: { isAdmin, liveIps, labelsForVersion },
    initialState: {
      pagination: { pageSize: 20 },
    },
    state: {
      sorting,
      globalFilter,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  // Show selected rows that live on OTHER pages at the top so they stay visible;
  // selected rows on the current page stay inline (just highlighted).
  const pageRows = table.getRowModel().rows;
  const pageRowIds = new Set(pageRows.map((row) => row.id));
  const offPageSelected = table
    .getFilteredSelectedRowModel()
    .rows.filter((row) => !pageRowIds.has(row.id));

  const renderRow = (row: Row<Box>) => (
    <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
      {row.getVisibleCells().map((cell) => (
        <TableCell key={cell.id}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  );

  const onAddToMonitoring = async () => {
    const selected = table.getSelectedRowModel().rows;
    if (!selected.length) return;
    try {
      await Promise.all(
        selected.map((row) => addAlertBox(row.original.ip, row.original.name))
      );
      toast.success(`Added ${selected.length} box(es) to monitoring`);
      table.resetRowSelection();
    } catch (error) {
      console.error('add to monitoring failed', error);
      toast.error('Failed to add to monitoring');
    }
  };

  // Send a command to the selected boxes, skipping any in live view (a command
  // would adb-disconnect and kill the scrcpy session).
  const sendCommand = (command: TaskCommand, ips: string[]) => {
    const allowed = ips.filter((ip) => !liveIps.has(ip));
    const skipped = ips.length - allowed.length;
    if (!allowed.length) {
      toast.error('Selected box(es) are in live view — commands blocked');
      return;
    }
    if (skipped) toast.message(`Skipped ${skipped} box(es) in live view`);
    createTask({ boxes: allowed, command, isTvControllerTask: false }).then(
      (id) => navigate(`/boxes/task/${id}`)
    );
  };

  // Global engineering-mode flag; external services read it from the public
  // GET /api/v1/engineering endpoint.
  const [engineeringConfig] = useEngineeringConfig();
  const onEngineeringToggle = async (enabled: boolean) => {
    try {
      await setEngineeringMode(enabled);
      toast.success(`Engineering mode ${enabled ? 'on' : 'off'}`);
    } catch (error) {
      console.error('engineering toggle failed', error);
      toast.error('Failed to update engineering mode');
    }
  };

  return (
    <div className="w-full pb-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Boxes</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 mr-4">
            <span className="text-sm text-muted-foreground">
              Engineering mode
            </span>
            <Switch
              checked={engineeringConfig?.enabled === true}
              onCheckedChange={onEngineeringToggle}
            />
          </div>
          <Button
            variant="outline"
            onClick={() =>
              sendCommand(
                TaskCommand.Ping,
                table
                  .getFilteredSelectedRowModel()
                  .rows.map((box) => box.getValue('ip') as string)
              )
            }
          >
            <RefreshCcw className="h-4 w-4" />
          </Button>
          {isAdmin && (
            <>
              <AddBoxDialog />
              <Button
                variant="outline"
                disabled={table.getSelectedRowModel().rows.length === 0}
                onClick={onAddToMonitoring}
              >
                Add to monitoring
              </Button>
            </>
          )}
          <ActionButton
            disabled={table.getSelectedRowModel().rows.length === 0}
            actions={actions}
            onActionSelected={(action: Action) => {
              if (action.value === TaskCommand.InstallApk) {
                setInstallApkOpen(true);
                return;
              }
              if (action.value === CC_ON || action.value === CC_OFF) {
                const names = table
                  .getSelectedRowModel()
                  .rows.map((row) => row.original.name);
                sendCcCommand(names, action.value === CC_ON)
                  .then(() =>
                    toast.success(
                      `CC ${action.value === CC_ON ? 'on' : 'off'} sent to ${names.length} box(es)`
                    )
                  )
                  .catch((error) => {
                    console.error('cc command failed', error);
                    toast.error('Failed to send CC command');
                  });
                return;
              }
              sendCommand(
                action.value as TaskCommand,
                table
                  .getSelectedRowModel()
                  .rows.map((box) => box.getValue('ip') as string)
              );
            }}
          />
        </div>
      </div>
      <Separator className="my-6" />
      <InstallApkDialog
        open={installApkOpen}
        onOpenChange={setInstallApkOpen}
        boxes={table.getSelectedRowModel().rows.map((row) => row.original)}
      />
      <TableToolbar table={table} />
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {offPageSelected.length || pageRows.length ? (
              <>
                {offPageSelected.map(renderRow)}
                {pageRows.map(renderRow)}
              </>
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between gap-4 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          {table.getSelectedRowModel().rows.length} of{' '}
          {table.getPreFilteredRowModel().rows.length} row(s) selected.
        </div>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium">Rows per page</p>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value) => table.setPageSize(Number(value))}
            >
              <SelectTrigger className="h-8 w-[80px]">
                <SelectValue
                  placeholder={`${table.getState().pagination.pageSize}`}
                />
              </SelectTrigger>
              <SelectContent side="top">
                {[20, 50, 100, 200].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm font-medium">
            Page {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount() || 1}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
